/**
 * What's New: a short note for each website release, in words a facility user understands.
 *
 * Help > What's New lists every entry, newest first. A pop-up (components/welcome/WhatsNewNotice)
 * shows an entry only when it is flagged `announce: true`, once per user.
 *
 * Rules for each release (see DEPLOY.md, "Release notes"):
 *  - Every website release that changes something a customer can see adds an entry, or adds items
 *    to today's entry. Nothing visible changed: add nothing.
 *  - Newest first. `id` is the date ('YYYY-MM-DD'); a second entry on the same day that must be
 *    announced on its own gets '-b', then '-c'. `date` is the same 'YYYY-MM-DD' (shown as words).
 *  - Each item: a short title and one or two short sentences. Add `helpArticle` (a slug from
 *    src/help/articles) when an article explains it; it shows as "Read more".
 *  - Customer words only: no issue or pull request numbers, no staff names, no internal names
 *    (see BANNED_WORDS; the content test fails on them). Say "the desktop program", never its
 *    technical name. No em dashes: use " - " or " · ".
 *  - `announce: true` only when the change alters how people do their work: a moved or renamed
 *    button, a new required step, a change to how printing or ordering works, or something they
 *    would otherwise call support about. Everything else stays quiet (leave `announce` out or false).
 *  - Releases are built from deploy/* branches (live + the change), so the entry must be in the
 *    commit that ships, not only on masternew.
 */

export interface ReleaseNoteItem {
  title: string;
  text: string;
  /** Help article slug (src/help/articles), linked as "Read more". */
  helpArticle?: string;
}

export interface ReleaseNote {
  /** 'YYYY-MM-DD', or 'YYYY-MM-DD-b' for a second entry the same day. Compared by releaseNotesModel.compareNoteIds. */
  id: string;
  /** 'YYYY-MM-DD'. */
  date: string;
  items: ReleaseNoteItem[];
  /** Show this entry in a pop-up once to each user. Rare: see the rules above. */
  announce?: boolean;
}

/**
 * Words that must never appear in a note (whole words, any case). These are internal names that
 * mean nothing to a facility, or that we do not show customers.
 */
export const BANNED_WORDS: string[] = [
  'Mike', 'Neil', 'Jeff', 'WinForms', 'VB', 'API', 'slot', 'slots', 'Mirth', 'GitHub', 'PR',
  'masternew', 'deploy', 'endpoint', 'migration', 'Keycloak', 'Azure', 'SQL', 'PRIME Manager',
  'React', 'cherry-pick', 'hotfix',
];

/** Newest first. */
export const RELEASE_NOTES: ReleaseNote[] = [
  {
    id: '2026-11-04',
    date: '2026-11-04',
    items: [
      {
        title: 'Resident list prints in the new layout',
        text: 'Print > Grid and Grid By Unit on Manage Residents now print the redesigned resident list: sorted by name, or by unit then room, with guests and a summary at the end. It is the same report as Resident Data Grid on the Reports page and lists every current resident; the search and filters on the screen do not narrow it.',
      },
      {
        title: 'Posting menus: a cleaner weekly layout',
        text: 'Weekly posting menus printed from the website use a new layout, with the meal names in a narrow column down the left side. To print the previous layout, turn off New layout on the report.',
      },
      {
        title: 'Posting menus: new designs',
        text: 'Posting - General Menu has a Large print week design (11x17, legal or letter paper), and the daily posting menus have a Today’s menu poster design with an optional QR code link. Pick them under Design when you print. Set the logo and accent color under Settings, Posting Menu, Designs.',
      },
      {
        title: 'Posting - General Menu uses your Temp Menu',
        text: 'When you pick a Temp Menu, Posting - General Menu now prints it, with its own name and days, whether or not the facility uses Production.',
      },
      {
        title: 'Reports: choose where to save an export',
        text: 'In Chrome and Edge, Export and Download PDF in the report viewer now open the Save As box, so you pick the folder and the file name. Other browsers download the file as before.',
      },
      {
        title: 'Menu Changes: asking PSI and extension sheets in one place',
        text: 'Request a Menu Change and Change Requests are now two tabs of one page, Menus > Menu Changes: Ask PSI, to ask for a change, and From extension sheets, to apply the changes marked on returned extension sheets. Old links and pinned pages still open them.',
        helpArticle: 'asking-for-a-menu-change',
      },
      {
        title: 'Deleted menus button on Menu Cycles',
        text: 'Recycle Bin is no longer listed under Menus. To restore a menu cycle or holiday menu that was deleted, select Deleted menus on the Menu Cycles page.',
      },
      {
        title: 'New menus from your menu service are set up by PSI',
        text: 'PSI now imports the menus from your menu service or consultant dietitian for you. When you have a new menu package, tell us with Ask PSI on Menus > Menu Changes, and we will build the new menu cycle.',
      },
      {
        title: 'Help on more pages',
        text: 'The Help button on Setup, Manage Residents, the user pages and the menu cycle view now opens the articles for that page.',
      },
      {
        title: 'Menu Import: new names for diet and texture columns, and a true preview',
        text: 'When a vendor menu uses a newer name (for example LCS in place of CCHO (LCS)), Menu Import can put the new name on the new menu in place of the old one. It reads these from the notes you paste and asks you to confirm each. Before building, the preview shows the week and each day\'s diet and texture columns exactly as they will be saved, and it warns when a new name has no AIM substitutions set up. After building, open the week\'s posting menu and extension sheet before you make the new menu active.',
      },
    ],
  },
  {
    id: '2026-11-03',
    date: '2026-11-03',
    items: [
      {
        title: 'Print Agents: download link for the installer',
        text: 'The Agent Registered box and the Registered Print Agents card now link to the print agent installer download.',
      },
      {
        title: 'Posting menus print without gaps or extra spaces',
        text: 'Weekly posting menus no longer leave blank lines under short items or extra spaces before items. Sub-items, such as the syrup under French Toast, start a little in from their item.',
      },
      {
        title: 'Posting menus: New layout and Indent sub-items',
        text: 'Posting menus have two new boxes, both checked. New layout prints the new row layout; clear it to print the posting menu as it looked before November 2026. Indent sub-items starts sub-items a little in from their item.',
      },
      {
        title: 'Diet Specific posting menus print garnish',
        text: 'Show Garnish on the Diet Specific posting menu now prints the garnish, and posting menus with Multiple Alt Sections no longer leave out alternates.',
      },
      {
        title: 'Recipe Book: just the Table of Contents or Index',
        text: 'Clear Recipes on the Recipe Book to print only the Table of Contents, the Index, or both, as plain tables that open in Excel one value per cell.',
      },
      {
        title: 'Reports show Queuing the moment you click',
        text: 'Generate and Print now show a Queuing message the moment you click, and a second click while the report is being queued does not queue it twice.',
      },
      {
        title: 'Print Queue shows Viewed and Printed right away',
        text: 'A report you open in the Print Queue now shows Viewed right away, and one you print shows Printed, instead of changing one report late.',
      },
      {
        title: 'Print agents get a random Agent ID',
        text: 'A print agent you register now gets a long random Agent ID instead of 1, 2, 3, so the number no longer shows how many agents you have. Print agents already installed keep working with no changes.',
      },
      {
        title: 'Report files keep the report name, and CSV export',
        text: 'Download PDF in the report viewer and Export in the Print Queue now save the file under the report\'s name instead of a random one. The viewer\'s Export menu also saves a report as CSV.',
      },
      {
        title: 'Tray Ticket Visualizer saves your changes first',
        text: 'When a resident or guest has unsaved changes, opening the Tray Ticket Visualizer now asks to save them first, then opens the ticket so it shows them. Cancel keeps your changes without opening it.',
      },
      {
        title: 'Save Only in Selective Touch',
        text: 'On the Selective Touch review, Save Only saves an order without printing it. An order you open and save without changing it starts on Save Only, so it is not printed again.',
      },
      {
        title: 'Regenerate keeps the saved order until Finish',
        text: 'Regenerate in Selective Touch now starts a meal over without deleting the saved order until you select Finish. Its message says plainly what will happen.',
      },
      {
        title: 'Credited meals ask only when credited',
        text: 'Selective Touch asks about modifying a credited meal only when the meal was actually credited or billed.',
      },
      {
        title: 'Print Queue Edit keeps Print Now',
        text: 'Editing a queued ticket from the Print Queue now opens it set to Print Now, as the queue intended.',
      },
      {
        title: 'Swap menu days writes each item once',
        text: 'A one-time swap or week menu made with Swap menu days no longer gets every item twice, which also stopped posting menus for those days from printing. If a swap ever does come out doubled, it is now refused and the menus are put back.',
      },
      {
        title: 'Recipe Book downloads to Excel',
        text: 'Downloading the Recipe Book to Excel now gives the whole book, one sheet per page, instead of a blank sheet.',
      },
      {
        title: 'Renaming a unit keeps its settings',
        text: 'Rename Unit now changes only the name. Before, renaming a unit also reset its sort order, colors, selective menu notes, room tray and service settings.',
      },
      {
        title: 'Nutrition Compliance dates and wording',
        text: 'Nutrition Compliance review dates and weight periods now follow your facility date in the evening, and weight findings point to where weight triggers are set on the website.',
      },
      {
        title: 'Toolbars fit narrow screens',
        text: 'On Assessments, Plan of Care, Nutrition Compliance, Items and Recipes, toolbar buttons stay in reach on narrow screens, and less used ones move under More actions.',
      },
      {
        title: 'Restored menu cycles show right away',
        text: 'A menu cycle restored from the Recycle Bin now shows on Menu Cycles right away, without reloading the page.',
      },
      {
        title: 'More Help articles with short videos',
        text: 'Help has many new articles with short narrated videos that play right in the article, each under the part it shows. They cover everything from Nutrition Compliance, assessments, guests and phones and tablets to items and recipes, special events, roles and access, facility setup, Getting Started, setup wizards, EHR, kitchen and reports.',
      },
      {
        title: 'Recipe amounts as fractions',
        text: 'Recipe ingredient amounts can now be typed as fractions, such as 2/3 or 1 1/2, and the exact amount is saved. Amounts show as kitchen fractions on the recipe screens, so 0.67 cup reads as 2/3 cup.',
      },
      {
        title: 'Basic menu day arrows go round the cycle',
        text: 'In the Basic menu editor, Next on the last day of the cycle goes to Day 1, and Previous on Day 1 goes to the last day. The date moves with them.',
      },
      {
        title: 'Reset Time on Day Change in the Basic menu',
        text: 'The Basic menu editor has a View option, Reset Time on Day Change, as in Menu Cycle Modify. It sets whether changing the day also moves to Morning, and it is on to start.',
      },
      {
        title: 'Items open on the screen you are on',
        text: 'Opening, adding or copying an item from another screen, such as the menu editors, adjustments, meal patterns and Auto Item Mods, now opens it over that screen instead of in a new browser tab.',
      },
      {
        title: 'Delete several combination overrides at once',
        text: 'In Menu Cycle Modify, on an override Combination Diet Override tab, tick the rows to remove and Delete removes them all after one question.',
      },
      {
        title: 'Facility tags on menu categories',
        text: 'A menu category set to Only for or Not for a facility now shows its facility tag in Menu Cycle Modify, as soon as you save.',
      },
      {
        title: 'Facility tags name every facility',
        text: 'Facility tags in Menu Cycle Modify now list every facility, so you can see the second one without pointing at the tag.',
      },
      {
        title: 'Paths show which facility each item is for',
        text: 'In Paths, items with the same name now show which facility each one is for, so you can pick the right one.',
      },
      {
        title: 'Selective Touch opens a saved order',
        text: 'In Selective Touch, a resident who already has an order for the meal now opens on that order, with no Existing Meal Ticket message to answer. To start over, select Clear All. A meal that was already credited or billed still asks first.',
      },
      {
        title: 'Recipe Composition reads the recipes your facility uses',
        text: 'In Menu Cycle Modify, Tools > Recipe Composition now checks each item against the recipe it uses at your facility, as the desktop program does. Items linked to a recipe for one facility only were skipped before, so more rows may now show as not matching.',
      },
      {
        title: 'Basic menu: arrows to move a day',
        text: 'In the Basic menu editor, Previous day and Next day arrows sit beside the Day box, so moving through the menu no longer needs the calendar. A new day opens on Morning, as in the desktop program.',
      },
      {
        title: 'New item and New From Existing in the Basic menu',
        text: 'The Items list in the Basic menu editor now has New item, and New From Existing beside each item it finds. The item opens over the menu, and once it is saved it goes on the day and meal you are on. Save the menu to keep it there.',
      },
      {
        title: 'Find Item shows the recipe your facility uses',
        text: 'In Menu Cycle Modify, the Recipe column in Find Item now shows the recipe each item uses at your facility. When an item was linked to a recipe for one facility only, Find showed no recipe, or a different one.',
      },
      {
        title: 'Selective Touch meals entered days ahead',
        text: 'In Selective Touch, a meal the resident is not on selective menu for is now faded and skipped by Next Meal, as on the desktop. If one is opened, the screen says why there is nothing to choose instead of only No menu items available. Add Other Date now starts on the meal on screen, and on an iPad the date and meal list fits on screen, scrolls, and marks Today.',
      },
      {
        title: 'Selective Touch date picker no longer stays grey',
        text: 'When a snack was started and not saved, the date and meal picker stayed grey for every resident until something was saved. Leaving the resident now drops the unsaved snack and goes back to the meal it came from. Snacks also no longer ask about an existing meal ticket.',
      },
      {
        title: 'Menu cycle overrides on a tablet',
        text: 'On a tablet, tapping a diet or consistency cell in the menu cycle now opens the cell menu, with Override, Clear, and Served or Not served, instead of changing the cell right away. A stray tap no longer removes an override. With a mouse, a click still changes the cell and a right-click opens the menu.',
      },
      {
        title: 'Side menu panel opens on iPads',
        text: 'On iPads held sideways and other screens about 1025 to 1280 pixels wide, the arrow at the bottom of the side menu now opens and closes the page panel on every page, including Print Queue.',
      },
      {
        title: 'Clearing the diets on a path now saves',
        text: 'In a menu cycle path, removing every diet from the list and pressing Save paths now updates the path. With Every diet, except the ones I pick and no diet left, the category is left off for every diet. With Only the diets I pick and no diet left, nothing is skipped and the path is not saved.',
      },
      {
        title: 'Users Logged In for your facilities',
        text: 'Users with the Users Logged In right can now open Users Logged In on the website. It lists who has been signed in at your facilities in the last 30 minutes, on the website or the desktop program, with the time each was last seen. Signed in markers older than 3 hours are now cleared automatically.',
      },
      {
        title: 'Override fill and Do Not Serve match the desktop program',
        text: 'In a menu cycle override, the fill button and Alt+F now bring in the item size and service item along with the item. In a combination override, Do Not Serve greys out the item fields and child items, and saving it keeps none of them, as in the desktop program. Alt+T and Alt+N move to the Diet Types and Consistencies lists.',
      },
      {
        title: 'Delete a menu change request',
        text: 'A menu change request that hasn\'t been started yet can now be deleted from Request a Menu Change. Once work has started on it, reply to it or call us instead.',
      },
      {
        title: 'Menu Import reads Diet Spreadsheet menus',
        text: 'Menu Import now reads a one-file Diet Spreadsheet menu with all its diet columns. Paste the notes that came with the menu (start date, week, new diet names) and they are read into settings you confirm. The menu is also checked for entries to look at before you build it. Nothing new is created from the notes: diets and consistencies must already exist.',
      },
    ],
  },
  {
    id: '2026-11-02',
    date: '2026-11-02',
    items: [
      {
        title: 'Item Usage for allergy groups',
        text: 'Tables, Allergy Groups has a new Item Usage button. It lists every item that carries the selected allergy group: set on the item, copied from its ingredient, or coming from an ingredient of its recipe. Search, print or export the list, and open any item from it. Searching the Items list by allergy group now also finds items that have more than one group.',
      },
      {
        title: 'Combination overrides list only the diet types on this menu',
        text: 'Add Diet Type in a combination override now lists only the diet types on this menu cycle, as in the desktop program. Alt+F fills the item in the override and combination override windows, and a copy made with New From Existing is titled Add a Diet Combination Override.',
      },
      {
        title: 'Extension Sheet schedules follow each menu cycle',
        text: 'A scheduled Extension Sheet saved with every diet type or every consistency checked now prints every column of the menu cycle in use on the day it runs, so diet types added later are included. A schedule saved with some boxes unchecked prints exactly what was checked.',
      },
      {
        title: 'Dates start on the current day at your facility',
        text: 'A new Special Event, a new survey session, the Nutrition Compliance sign-off and its reports now default to the current date at your facility. In the evening they no longer jump ahead to the next day. The Overdue flag in Receiving uses the facility date too.',
      },
      {
        title: 'Assessment Only menu',
        text: 'With an Assessment Only license, Recipes and Item Linking are left out of the menu, as in the desktop program.',
      },
      {
        title: 'Page names on browser tabs',
        text: 'The Order Guide/Shelf Assignment and Guests pages now show their own names on the browser tab.',
      },
      {
        title: 'One calendar for every date box',
        text: 'Every date box on the website now uses the same date field and calendar, including Menu Swap, Copy in the menu cycle, the basic menu cycle editor, Plan of Care, Nutritional Analysis and the phone screens for Global Adjustments, Global Meal Patterns and Special Events. Type the date with or without slashes, or pick it from the calendar.',
      },
      {
        title: 'Today button and desktop keys in the calendar',
        text: 'The calendar now has a Today button, and a Clear button on dates that may be left empty. F4 or Alt+Down opens the calendar, and F4, Alt+Up or Escape closes it, as in the desktop program. Escape closes only the calendar, not the window behind it, and Tab stays inside the calendar while it is open.',
      },
      {
        title: 'Recipe nutrition shows every nutrient',
        text: 'The Nutrition tab on a recipe now lists every nutrient with its calculated value, any override, the unit and the DRI percent, and points out ingredients that count as zero. Saving a recipe no longer clears the nutrient overrides on it.',
      },
      {
        title: 'Adjustments keep their item size',
        text: 'Opening a saved adjustment, in Global Adjustments or on a resident, no longer changes its Item Size to the default size of the item. Saving it again keeps the size that was saved.',
      },
      {
        title: 'Combination overrides start from the override item',
        text: 'In a menu cycle override, the fill button beside Item in a combination override now brings in the override item instead of the original menu item. New starts with the consistency from the override, and Add Diet Type and Add Consistency put a check mark on the ones on this menu.',
      },
      {
        title: 'Item Linking under Food Data',
        text: 'Item Linking is now also under Food Data, Items and Recipes, next to the item list, as in the desktop program. It is still under Tables as well.',
      },
      {
        title: 'Menu preview shows item types and combinations',
        text: 'Menu cycle preview lines now show the item type after each item name. The hover over a menu cell keeps showing combination overrides after you save an override, and long hovers no longer cut off their last lines.',
      },
      {
        title: 'Clear all and Clear in the menu cycle',
        text: 'Clear all now clears the override on screen right away, the same as Clear. The only difference is the check: Clear all also unchecks the consistency, Clear keeps it checked. Both now show a hint that says so.',
      },
      {
        title: 'Extension Sheets list only the menu cycle diets',
        text: 'When you print Extension Sheets, the diet type and consistency lists now show only the ones on the menu cycle, all checked. Select Active checks the ones in current resident diet orders. What prints is unchanged.',
      },
      {
        title: 'Support chat moves out of the way',
        text: 'Minimizing the support chat now puts it in the bottom strip, so it no longer sits over the page or covers the Support Chat button in the strip.',
      },
      {
        title: 'Allergy group printing: a tidier window',
        text: 'Tables, Allergy Groups, Print now opens a narrower window with each set of choices in its own list. What prints is unchanged.',
      },
      {
        title: 'Global Adjustments on a phone',
        text: 'On a phone, Global Adjustments now shows each adjustment as a card that says what it does, when it applies and how many residents it reaches, with a page of its own for the details. Importing from a menu cycle and deleting several at once stay on a tablet or computer.',
      },
      {
        title: 'Meal Pattern Groups and Special Events on a phone',
        text: 'On a phone, Meal Pattern Groups and Special Events now show as cards you can search, open and edit, instead of a grid cut off at the side. Special Events lists upcoming events first.',
      },
      {
        title: 'Resident tabs and menu screens fit a phone',
        text: 'On a phone, the buttons on Weight History and other resident tabs no longer run off the screen, so you can add a weight from a phone. Forecasting, Menu Cycles, the Basic menu editor and Intake Studies also fit the screen better.',
      },
      {
        title: 'Request a Menu Change: add only what differs',
        text: 'Asking for a menu change no longer shows a grid of every diet and consistency. Choose Add a diet or consistency only when one needs something different, and say what they get.',
      },
      {
        title: 'Selective Touch: Build from Menu keeps your tiles',
        text: 'In Settings, Selective Touch, Page Layout, Build from Menu now keeps the tiles already on the page with their settings and opens on the cycle you serve today. Tiles set to sort by menu order now list items in menu order, main items first, as in the desktop program.',
      },
      {
        title: 'Selective Touch: date and meal tiles are readable',
        text: 'In Select Date & Meal, every date and meal tile now shows its text. Before, the tiles other than the selected one could look blank.',
      },
      {
        title: 'Swap menu days keeps your set-up when you switch',
        text: 'Switching between Just these dates and Change the cycle keeps the days and the items you picked. The dates become the cycle days they fall on, and Change the cycle can pick a cycle day by date.',
      },
      {
        title: 'Column filters keep only what your search finds',
        text: 'When you type in a column filter\'s search box, OK now keeps only the ticked values the search shows. Before, values hidden by the search stayed ticked, so the list did not narrow.',
      },
      {
        title: 'Close a report with the X',
        text: 'The report viewer now closes with the X at the top right, as other windows do, instead of the back arrow at the top left.',
      },
    ],
  },
  {
    id: '2026-11-01',
    date: '2026-11-01',
    items: [
      {
        title: 'Ask PSI for a menu change',
        text: 'Under Menus, Request a Menu Change lets you ask PSI to change an item on a meal or swap two meals, with a note and the diet and consistency marks if you want. Your requests and PSI\'s replies are listed on the same page.',
      },
      {
        title: 'Report options fit small screens',
        text: 'On a phone or in a narrow window, report drop-down lists such as Print Type and Paper Type on Tray Tickets now fit inside their boxes instead of running off the edge.',
      },
      {
        title: 'Nutrient totals show what is missing',
        text: 'Nutritional Analysis now marks items that count as zero or only partly (an empty recipe, an ingredient with no nutrient values, or an amount like "1 each" with no weight) and lists which ones and why, so a low total is labeled as partial.',
      },
      {
        title: 'Validation: Missing Nutrient Data',
        text: 'Validation has a new Missing Nutrient Data tab listing the menu items, recipes and ingredients that count as zero or only partly in nutrient totals, and the menu items that need a recipe, the most served first. Every menu item should be a recipe whose ingredients have nutrient data and a weight; Validation now warns about items that are not.',
      },
      {
        title: 'Make it a recipe',
        text: 'On Validation, tick menu items linked straight to a USDA food and choose Make it a recipe: each gets a recipe with that one ingredient and the same nutrients as before. An item served in a size with no weight is listed as needing a weight instead.',
      },
      {
        title: 'Total Fat and Vitamin A',
        text: 'Total Fat now uses the total fat value stored for each ingredient where there is one, instead of adding up only saturated, mono and poly fats, which read low. Vitamin A (RAE) is now labeled in micrograms.',
      },
      {
        title: 'AI Assistant removed for now',
        text: 'The AI Assistant chat is no longer on the website. If you used it, contact PSI support.',
      },
    ],
  },
  {
    id: '2026-10-31',
    date: '2026-10-31',
    items: [
      {
        title: 'Reports open full screen',
        text: 'A report now fills the whole window when you open it, so you see more of each page. The back arrow at the top left returns you to the screen you came from (it replaces the X).',
      },
      {
        title: 'PDPM is now Nutrition Compliance',
        text: 'The PDPM page is now Nutrition Compliance: nutrition findings with the evidence behind each one, weight data to review, MDS Section K review, documentation readiness, sign-off of the checks by your dietitian, and printable reports. It shows no dollar amounts. Your administrator gives access under Nutrition Compliance.',
      },
    ],
  },
  {
    id: '2026-10-30',
    date: '2026-10-30',
    items: [
      {
        title: 'Production Tally: each cart on its own pages',
        text: 'Pick two or more carts on Specific Cart and tick "Each cart on its own pages" to print one report with a section for each cart. The cart list also shows how many residents are on each cart, and empty carts are greyed out.',
      },
      {
        title: 'Menu Cycle Sync Options: filter the list',
        text: 'Sync Options: you can now filter the list by Day, Mealtime, Menu Category, Size, Consistency, Item Type and each diet column.',
      },
      {
        title: 'Menu Cycle Sync Options: facility exceptions shown',
        text: 'Rows that are only for, or not for, certain facilities now say so under the row (for example "Only for Parker at Somerset"), so you can see it before you copy or sync. Copy brings that exception with it.',
      },
      {
        title: 'New Resident: pick the room and bed',
        text: 'Where the facility requires room and bed selection, the New Resident window now lists the unit\'s rooms and each room\'s beds to pick from, as the resident profile does.',
      },
      {
        title: 'New Resident: Create and Open Profile is the main button',
        text: 'On the last step, Create and Open Profile is now the main button (Enter presses it), so the new resident\'s profile opens straight away. Create Resident is still there beside it.',
      },
      {
        title: 'Typing dates: the slashes are added for you',
        text: 'In any date box, type just the numbers (for example 08101967) and the slashes go in as you type. A birth date typed with a two-digit year, like 67, is read as 1967, and a birth date in the future is refused.',
      },
      {
        title: 'Menu Cycle Modify: back to where you were',
        text: 'A Back button beside the day arrows (or the B key) returns to the day and mealtime you were on before, with the same row selected. After going back, Forward (Shift+B) goes again.',
      },
      {
        title: 'Phones: Touch on the tab bar',
        text: 'On a phone, the tab bar at the bottom of the screen now has Touch (Selective Touch) where Kitchen was. Kitchen is still in More.',
      },
      {
        title: 'Phones and tablets: easier to use',
        text: 'Many pages fit small screens better, including with the phone held sideways: larger buttons and boxes to tap, readable text, and lists and editors that scroll as a whole. This covers Selective Touch, Bedside and Self Order, the item, recipe and resident pages, Reports and Help.',
      },
      {
        title: 'Global Meal Patterns on a phone',
        text: 'On a phone, Global Meal Patterns is now a list of cards: tap one to see it, and add or change a pattern in a sheet that fits the screen.',
      },
      {
        title: 'Kitchen: diet colors always readable',
        text: 'A diet with no colors set no longer shows as black text on black on the Kitchen page.',
      },
    ],
  },
  {
    id: '2026-10-29',
    date: '2026-10-29',
    items: [
      {
        title: 'Nutritional Analysis report: up to 12 nutrients',
        text: 'If you pick more than 12 nutrients and print the report, you are now told which ones the printed report will leave out before it opens.',
      },
    ],
  },
  {
    id: '2026-10-28',
    date: '2026-10-28',
    items: [
      {
        title: 'Viewed in the Print Queue',
        text: 'A report you open on screen now shows as Viewed until you print it, then Printed. The desktop program shows these reports as Printed.',
        helpArticle: 'favorites-and-print-queue',
      },
      {
        title: 'Posting menus no longer blank',
        text: 'Posting menus run from the website now print with their menu text.',
      },
      {
        title: 'Paste into a new menu row',
        text: 'In a menu cycle, when you add a row with Ctrl+1 to Ctrl+4 after cutting or copying an item, press Ctrl+V or choose Paste here to put that item in the new row.',
        helpArticle: 'changing-menu-items',
      },
      {
        title: 'Delete several global items at once',
        text: 'On Global Meal Patterns and Global Adjustments, check the rows you want and delete them in one step.',
      },
      {
        title: 'Drop-down boxes select their text',
        text: 'Clicking into a drop-down box now selects what is in it, so you can type over it right away.',
      },
    ],
  },
  {
    id: '2026-10-27',
    date: '2026-10-27',
    items: [
      {
        title: 'One click to change a menu item',
        text: "In the menu cycle editor, with editing on, one click on an item's name or size opens it for editing, as in the desktop program. Sizes show as fractions, such as ⅔ c.",
        helpArticle: 'changing-menu-items',
      },
      {
        title: 'Straight to your organization',
        text: 'If you belong to only one organization, signing in now takes you straight to it.',
      },
      {
        title: 'Open another organization in a new window',
        text: 'The new window now signs you in by itself and shows your organizations, instead of asking you to sign in again.',
      },
      {
        title: 'Report options say why they are off',
        text: 'When a report option is greyed out, it now tells you why.',
      },
      {
        title: 'Easier scroll bar in the report viewer',
        text: "The report viewer's scroll bar is wider and easier to grab.",
      },
    ],
  },
  {
    id: '2026-10-26',
    date: '2026-10-26',
    items: [
      {
        title: 'Analysis on one page',
        text: 'The Analysis screens are now one Analysis page. Review diet orders, allergies, special needs and eating devices together, and see who is behind any number.',
        helpArticle: 'using-analysis',
      },
      {
        title: 'Menu cycle help articles',
        text: 'Help now has ten step-by-step articles on menu cycles, from reading a menu cycle to holiday menus and menu swaps.',
        helpArticle: 'menu-cycles',
      },
      {
        title: 'Favorites stay put',
        text: 'The Favorites group in the Reports panel no longer jumps around while it loads.',
      },
    ],
  },
];
